import { BooleanPropertyDTO } from 'business/models/Models';
import LoadingDiv from 'components/generic/loadingDiv';
import { useModalHandlers } from 'components/modal';
import { SearchAndFilterReportsDialogue } from 'components/reports/SearchAndFilterReportsDialogue';
import { showPopupBlockedModal } from 'components/reports/utils';
import { Props as ITabPanelProps } from 'components/tab/headless/tabPanel';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { systemPropertiesCache } from 'generic/systemPropertiesCache';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useFetchsearchGroupDetails } from 'redux/hooks/common';
import {
  useCurrentSelectedWellDropDetails,
  useFetchDrawingPreferences,
  useFetchSearchDetails,
  useFetchSearchFilters,
  useFetchSearchTargetCharts,
  useFetchSearchTargetFieldsMap,
  useFetchSearchTargets,
  useIsFetchingSearchDetails,
  useIsSearchInProgress,
  usePerformSearch,
  useSearchDetails,
  useSearchResult,
} from 'redux/hooks/searchnFilter';
import { IStyle } from 'Styles';
import Analytics from './analytics';
import BottomMenu from './bottomMenu/bottomMenu';
import Canvas from './canvas';
import Drop from './drop';
import SNFHeader from './header';
import { useSnfKeyboardHandler } from './hooks';
import Result from './result';
import Search from './search';
import { useSidePanelContent } from './sidePanel/hooks';
import { getSnfWellKey } from './utils';
import {
  bottomMenuAllowedRoutes,
  ESnfRouteSegment,
  getSnfRoutePath,
  snfDropRoutes,
  snfResultsDependentRoutePattern,
} from './constants';

let zoomInvokerDelegate;

function setZoomInvocker(functionDelegate) {
  zoomInvokerDelegate = functionDelegate;
}

const SNFContainer = () => {
  const { id: idStr } = useParams();
  const id = parseInt(idStr, 10);
  const navigate = useNavigate();
  const location = useLocation();
  const [tabSelectors, tabContent] = useSidePanelContent();
  const fetchSearchDetails = useFetchSearchDetails();
  const searchDetails = useSearchDetails();
  const fetchSearchFilters = useFetchSearchFilters();
  const isfetchingDetails = useIsFetchingSearchDetails();
  const fetchDrawingPreferences = useFetchDrawingPreferences();
  const performSearch = usePerformSearch();
  const searchResult = useSearchResult();
  const isSearchInProgress = useIsSearchInProgress();
  const enableCanvasPref = useSystemProperty(
    PropertyConstants.SeachFilterEnableCanvas,
    false,
  );
  const isCanvasEnabled = (enableCanvasPref as BooleanPropertyDTO)?.value !== false;
  const isCanvasRoute = bottomMenuAllowedRoutes.some((substring) =>
    window.location.pathname.includes(substring),
  );
  const isDropMode = snfDropRoutes.some((route) =>
    window.location.pathname.includes(route),
  );
  const [reRenderKeyOnDiscard, setReRenderKeyOnDiscard] = useState(1);
  const [reportType, setReportType] = useState<string>('');
  const [showReportDialogue, setShowReportDialogue] = useState<boolean>(false);
  const [isSearchDefinitionReady, setIsSearchDefinitionReady] = useState(false);
  useSnfKeyboardHandler(zoomInvocker);
  const wellRefs = useRef<{ [key: string]: any }>({});
  const selectedWell = useCurrentSelectedWellDropDetails();
  const fetchSearchTargets = useFetchSearchTargets();
  const fetchSearchTargetFieldsMap = useFetchSearchTargetFieldsMap();
  const FetchScoreGroupDetails = useFetchsearchGroupDetails();
  const fetchSearchTargetCharts = useFetchSearchTargetCharts();
  const chartInstance = useRef(null);
  const { showModal: showModalSimple, hideModal } = useModalHandlers();
  const showFileNotFoundModal = useFileNotFoundModal();
  const [popupBlockedModal, setPopupBlockedModal] = useState(false);

  useEffect(() => {
    setIsSearchDefinitionReady(false);
    let cancelled = false;

    (async () => {
      try {
        await Promise.all([
          fetchSearchDetails(id),
          fetchSearchFilters(id),
          fetchSearchTargets(),
          systemPropertiesCache
            .get(PropertyConstants.SearchFilterPageSize)
            .catch(() => null),
        ]);
      } finally {
        if (!cancelled) {
          setIsSearchDefinitionReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!isSearchDefinitionReady) {
      return;
    }
    if (!snfResultsDependentRoutePattern.test(location.pathname)) {
      return;
    }
    if (!searchDetails.id || searchDetails.id < 0 || searchDetails.errorCode === 404) {
      return;
    }
    if (isSearchInProgress || searchResult.rows.items.length > 0) {
      return;
    }
    performSearch();
  }, [isSearchDefinitionReady, location.pathname]);

  useEffect(() => {
    fetchDrawingPreferences();
    FetchScoreGroupDetails();
    fetchSearchTargetFieldsMap();
    fetchSearchTargetCharts();
  }, []);

  useEffect(() => {
    if (reportType != '') {
      setShowReportDialogue(true);
    } else {
      setShowReportDialogue(false);
    }
  }, [reportType]);

  useEffect(() => {
    if (
      id &&
      enableCanvasPref &&
      (enableCanvasPref as BooleanPropertyDTO).value === false &&
      isCanvasRoute
    ) {
      navigate(getSnfRoutePath(id, ESnfRouteSegment.Result), { replace: true });
    }
  }, [id, enableCanvasPref, isCanvasRoute, navigate]);

  if (!isSearchDefinitionReady || isfetchingDetails) {
    return <LoadingDiv />;
  }

  if (searchDetails.errorCode == 404) {
    showFileNotFoundModal();
    return;
  }

  if (searchDetails?.searchName) {
    document.title = searchDetails?.searchName;
  }

  const sidePanelWidth =
    (Array.isArray(tabContent)
      ? (tabContent as React.ReactElement<ITabPanelProps>[])[0]?.props?.width
      : (tabContent as React.ReactElement<ITabPanelProps>)?.props?.width) ?? 30;

  function zoomInvocker(zoomIn) {
    if (
      isDropMode &&
      selectedWell?.regionID &&
      selectedWell?.cpID &&
      selectedWell?.imageBatchId
    ) {
      triggerZoom(getSnfWellKey(selectedWell), zoomIn);
    } else {
      if (zoomInvokerDelegate) {
        zoomInvokerDelegate(zoomIn);
      }
    }
  }

  const setWellRef = (wellKey: string, ref: any) => {
    if (ref) {
      wellRefs.current[wellKey] = ref;
    } else {
      delete wellRefs.current[wellKey];
    }
  };

  const triggerZoom = (wellKey: string, zoomIn) => {
    if (wellRefs.current[wellKey]) {
      wellRefs.current[wellKey].ZoomHandler(zoomIn);
    }
  };
  return (
    <>
      <SNFHeader
        tabSelectors={tabSelectors}
        setReRenderKeyOnDiscard={setReRenderKeyOnDiscard}
        setReportType={setReportType}
        chartInstance={chartInstance}
      />
      <div id="snfcontainer-content-container" className="flex w-full h-full">
        <div
          id="snfcontainer-routes-wrapper"
          className={`flex  w-[${100 - sidePanelWidth}%]`}
        >
          <Routes>
            <Route index element={<Search />} key={reRenderKeyOnDiscard} />
            <Route
              path={ESnfRouteSegment.Result}
              element={
                <div id={`snfcontainer-content-result`} className="h-[90vh] w-full">
                  <Result setReportType={setReportType} />
                </div>
              }
            />
            <Route
              path={ESnfRouteSegment.Canvas}
              element={<Canvas setZoomInvocker={setZoomInvocker} />}
            />
            <Route
              path={ESnfRouteSegment.Analytics}
              element={<Analytics chartInstance={chartInstance} />}
            />
            <Route
              path=":wellMode?"
              element={<Drop setZoomInvocker={setZoomInvocker} setWellRef={setWellRef} />}
            />
          </Routes>
        </div>

        {tabContent && (
          //@ts-ignore
          <div id="snfcontainer-sidepanel" style={styles.sidePanelRoot(sidePanelWidth)}>
            {tabContent}
          </div>
        )}
      </div>
      {isCanvasRoute && isCanvasEnabled && <BottomMenu zoomInvocker={zoomInvocker} />}

      {showReportDialogue && (
        <SearchAndFilterReportsDialogue
          reportType={reportType}
          setReportType={setReportType}
          onPopupBlocked={() => {
            setPopupBlockedModal(true);
          }}
        />
      )}
      {popupBlockedModal &&
        showPopupBlockedModal(showModalSimple, setPopupBlockedModal, hideModal)}
    </>
  );
};

const styles: IStyle = {
  //@ts-ignore
  sidePanelRoot: (width: number) => ({
    borderLeft: '1px solid rgb(159 159 159)',
    backgroundColor: 'white',
    width: `${width}%`,
  }),
};

export default SNFContainer;
